import {
  useEffect,
  useId,
  useRef,
  type ReactNode,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import {
  DRAWER_FOCUSABLE_SELECTOR,
  nextFocusIndex,
} from '@/components/ui/drawerFocus'

type DrawerProps = {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  side?: 'right' | 'left'
  /** Width class, default w-full max-w-md */
  widthClass?: string
}

/**
 * Minimal side drawer with Escape + basic Tab focus cycle.
 * Not wired to pages yet (A3/A5 primitives).
 */
export function Drawer({
  open,
  onClose,
  title,
  children,
  side = 'right',
  widthClass = 'w-full max-w-md',
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const titleId = useId()

  useEffect(() => {
    if (!open) return
    const prev = document.activeElement as HTMLElement | null
    const panel = panelRef.current
    const focusables = panel?.querySelectorAll<HTMLElement>(DRAWER_FOCUSABLE_SELECTOR)
    focusables?.[0]?.focus()

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
        return
      }
      if (e.key !== 'Tab' || !panel) return
      const nodes = [
        ...panel.querySelectorAll<HTMLElement>(DRAWER_FOCUSABLE_SELECTOR),
      ].filter((el) => !el.hasAttribute('disabled'))
      if (nodes.length === 0) return
      const active = document.activeElement as HTMLElement | null
      let idx = nodes.findIndex((n) => n === active)
      if (idx < 0) idx = 0
      e.preventDefault()
      const next = nextFocusIndex(idx, nodes.length, e.shiftKey)
      nodes[next]?.focus()
    }

    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      prev?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null

  const sidePos = side === 'right' ? 'right-0' : 'left-0'

  return (
    <div className="fixed inset-0 z-50 flex" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/40"
        aria-label="Đóng panel"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`absolute top-0 ${sidePos} flex h-full ${widthClass} flex-col border-[var(--color-border)] bg-[var(--color-surface)] shadow-[0_10px_15px_-3px_rgba(33,49,69,0.12)] ${
          side === 'right' ? 'border-l' : 'border-r'
        }`}
        onKeyDown={(e: ReactKeyboardEvent) => {
          if (e.key === 'Escape') onClose()
        }}
      >
        <div className="flex h-[var(--topbar-height)] items-center justify-between border-b border-[var(--color-border)] px-4">
          <h2 id={titleId} className="text-sm font-semibold text-[var(--color-text)]">
            {title}
          </h2>
          <Button size="sm" variant="ghost" onClick={onClose} aria-label="Đóng">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-4">{children}</div>
      </div>
    </div>
  )
}
